import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import axios from 'axios';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleLoader, ErrorScreen } from 'src/components';
import { Loading } from 'src/components/Loading';
import { EndPoints } from 'src/config';
import { currencySymbolMapping } from 'src/helpers';
import { useRejectClientDeliveryRequestByUser } from './hooks';
import { useClientDeliveryRequestFetchInfoToConfirmAction } from './useClientDeliveryRequestFetchInfoToConfirmAction';

export type Props = {
  token: string;
};

export const ClientDeliveryCustomerConfirmation: FC<Props> = ({ token }) => {
  const { t } = useTranslation();

  const {
    submitClientDeliveryRequest,
    isLoading: isRejecting,
    error: rejectError,
    data: rejectData,
  } = useRejectClientDeliveryRequestByUser();

  const { fetchData, isLoading, error, data } = useClientDeliveryRequestFetchInfoToConfirmAction();
  const [isCreatingStripeSession, setIsCreatingStripeSession] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);

  useEffect(() => {
    fetchData(token);
  }, [fetchData, token]);

  const handleStripePayment = useCallback(async () => {
    try {
      setIsCreatingStripeSession(true);
      const response = await axios.post(EndPoints.clientDeliveryPaymentCreateStripCheckoutSession, {
        token,
        successRedirectUrl: `${window.location.origin}/clientDelivery/paymentSuccess/${token}`,
        cancelUrl: `${window.location.origin}/clientDelivery/paymentCanceled/${token}`,
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error redirecting to Stripe:', error);
    } finally {
      setIsCreatingStripeSession(false);
    }
  }, [token]);

  const retryReload = useCallback(() => {
    fetchData(token);
  }, [fetchData, token]);

  const handleRejectRequest = useCallback(() => {
    setOpenRejectModal(true);
  }, []);

  const confirmRejectRequest = useCallback(() => {
    submitClientDeliveryRequest(token);
    setOpenRejectModal(false);
    // Add reject logic here
  }, [submitClientDeliveryRequest, token]);

  if (isLoading || isRejecting) {
    return <Loading />;
  }

  if (rejectError) {
    return <ErrorScreen error={rejectError} retry={retryReload} />;
  }

  if (rejectData) {
    return (
      <div className="flex flex-col items-center min-h-screen bg-gray-50 py-10 px-4">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg">
          <div className="bg-blue-600 text-white py-4 px-6 rounded-t-lg">
            <h1 className="text-xl font-bold">{t('client-delivery-requests.page-title')}</h1>
          </div>
          <div className="p-6 space-y-6">
            <p className="text-gray-700 text-sm">{t('client-delivery-requests.rejected-message')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return <ErrorScreen error={error ?? new Error('Aucune demande trouvé')} retry={retryReload} />;
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-50 py-10 px-4">
      {/* Header Section */}
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg">
        <div className="bg-blue-600 text-white py-4 px-6 rounded-t-lg">
          <h1 className="text-xl font-bold">{t('client-delivery-requests.page-title')}</h1>
        </div>
        <div className="p-6 space-y-6">
          {/* Greeting and Info */}
          <p
            className="text-gray-700 text-sm"
            dangerouslySetInnerHTML={{
              __html: t('client-delivery-requests.hello', {
                name: data.name || t('client-delivery-requests.no-request-found'),
              }),
            }}
          />
          <p className="text-gray-700 text-base">
            {t('client-delivery-requests.approved-message', { agencyName: data.shipping.toAgency.name })}&nbsp;
          </p>

          {/* Proposed Price */}
          <p className="text-gray-800">
            {t('client-delivery-requests.price-details')} &nbsp;
            <b className="text-black">
              {t('client-delivery-requests.price-with-currency', {
                price: data.priceAfterDiscount,
                currency: currencySymbolMapping(data.currency),
              })}
            </b>
          </p>
          {!!data.usedPromotionProgram && (
            <p className="text-sm text-black">
              Au lieu de{' '}
              <span className="text-red-500 line-through font-bold">
                {data.organizationSuggestedPrice}
                {currencySymbolMapping(data.currency)}
              </span>{' '}
              Grâce au code promotion <b className="text-green-600">{data.usedPromotionProgram.code}</b>
            </p>
          )}

          {/* Agency Note */}
          {!!data.organizationSuggestedPriceNote && (
            <div className="bg-gray-100 rounded-md p-4 border-l-4 border-blue-500">
              <p className="text-gray-600 text-base">
                <strong>{t('client-delivery-requests.agency-note')}</strong>
              </p>
              <p className="text-sm text-gray-600 italic">{data.organizationSuggestedPriceNote}</p>
            </div>
          )}

          {/* Delivery Information */}
          <div className="bg-gray-100 rounded-md p-4 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600">
              <strong>{t('client-delivery-requests.shipping-number')}</strong> {data.shipping.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>{t('client-delivery-requests.request-number')}</strong> {data.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>{t('client-delivery-requests.agency')}</strong> {data.shipping?.toAgency?.name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>{t('client-delivery-requests.your-address')}</strong>{' '}
              {data.address || t('client-delivery-requests.no-address-found')}
            </p>
          </div>

          {/* Payment Methods */}
          {!isCreatingStripeSession ? (
            <div className="space-y-4">
              {/* Primary CTA - Pay with Card */}
              <button
                onClick={handleStripePayment}
                className="w-full bg-green-600 inline-block text-white py-4 px-6 rounded-lg text-center shadow-lg focus:outline-none"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M2.99968 9.29986H20.9997M4.80015 5.10001H19.1998C20.5252 5.10001 21.5997 6.17369 21.5998 7.49914L21.6 16.501C21.6001 17.8265 20.5255 18.9 19.2001 18.9L4.80038 18.8998C3.47493 18.8998 2.40044 17.8254 2.4004 16.4999L2.40015 7.50008C2.40011 6.17457 3.47464 5.10001 4.80015 5.10001Z"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span className="font-semibold text-lg">Payer avec une carte de crédit</span>
                </div>
                <p className="text-sm mt-1 text-blue-100">Rapide et sécurisé avec Stripe</p>
              </button>

              {/* Secondary CTA - Reject Request */}
              <button
                onClick={handleRejectRequest}
                className="w-full bg-red-500 text-white py-3 px-6 rounded-lg text-center shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
              >
                Refuser la demande
              </button>
            </div>
          ) : (
            <CircleLoader />
          )}

          {/* Confirmation Note */}
        </div>
      </div>

      {/* Reject Confirmation Modal */}
      <Dialog
        open={openRejectModal}
        onClose={() => setOpenRejectModal(false)}
        aria-labelledby="reject-dialog-title"
        aria-describedby="reject-dialog-description"
      >
        <DialogTitle id="reject-dialog-title">{t('global.confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="reject-dialog-description">
            {t('client-delivery-requests.reject-confirmation-text')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectModal(false)} color="primary">
            {t('global.cancel')}
          </Button>
          <Button onClick={confirmRejectRequest} color="secondary" autoFocus>
            {t('global.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
